import { useRef, useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';

import { setAlert } from '../actions/defaultActions';
import { siteboardAction } from '../actions/defaultActions';
import { Email, ADD_PROJECT_MEMBER, SET_USER_ROLE_INFO_DIALOGUE_SHOWING, SET_EDIT_PROJECT_MEMBERS_DIALOGUE_ID } from '../actions/types';
import { objectKeysToList } from '../Util';

import PopupWindow from './PopupWindow';
import MemberRolePicker from './MemberRolePicker';
import ProjectMembers from './ProjectMembers';

// icon imports
import EmailIcon from '../icons/emailIcon';
import InfoIcon from '../icons/infoIcon';
import CircleCheckIcon from '../icons/circleCheckIcon';
import AddIcon from '../icons/addIcon';
import SendIcon from '../icons/sendIcon';

const AddProjectMemberDialogue = ({ _projects, _users, _editProjectMembersDialogueId, _subscriptions, setAlert, siteboardAction }) => {
  const inputEmailRef = useRef(null);

  const [selectedRole, setSelectedRole] = useState('');
  const [inputEmail, setInputEmail] = useState('');
  const [inviteSentConfirmation, setInviteSentConfirmation] = useState(false);
  const [userAddedConfirmation, setUserAddedConfirmation] = useState(false);

  const closeDialogue = useCallback(() => {
    siteboardAction(SET_EDIT_PROJECT_MEMBERS_DIALOGUE_ID, { projectId: '' });
  }, [siteboardAction]);

  const selectedProject = _editProjectMembersDialogueId in _projects ? _projects[_editProjectMembersDialogueId] : null;
  if(selectedProject === null) return (<></>);

  const suggestedEmailsMap = {};
  const projectMemberEmailsMap = {};
  const projectPendingMemberEmailsMap = {};
  const subscriptionMemberEmailsMap = {};
  try {
    for(const userId in _subscriptions[selectedProject.subscriptionId].members) {
      subscriptionMemberEmailsMap[_users[userId].email] = '';
    }
  } catch(error) {}
  for(const email in selectedProject.pendingMembers) {
    projectPendingMemberEmailsMap[email] = '';
  }
  for(const userId in selectedProject.members) {
    if(!(userId in _users)) continue;
    projectMemberEmailsMap[_users[userId].email] = '';
  }
  for(const userId in _users) {
    const email = _users[userId].email;
    if(!(email in projectMemberEmailsMap) && !(email in projectPendingMemberEmailsMap)) {
      suggestedEmailsMap[_users[userId].email] = '';
    }
  }
  const suggestedEmails = objectKeysToList(suggestedEmailsMap);
  const inputEmailIsSubscriptionMember = inputEmail.trim() in subscriptionMemberEmailsMap;
  const inputEmailAlreadyAdded = inputEmail.trim() in projectMemberEmailsMap;
  const inputEmailAlreadyInvited = inputEmail.trim() in projectPendingMemberEmailsMap;

  return (
    <PopupWindow title='Project Members' closeDialogue={closeDialogue} bgStyle='bg-[#333333]'>
      <div className='flex flex-col gap-8 max-w-screen-lg text-white'>
        <div className='w-[90vw] max-w-full' />
        {/* project members */}
        <div>Assign members for project <b>"{selectedProject.name}"</b>.</div>
        {/* Invite by email */}
        <div className='flex flex-col gap-y-4'>
          <div>
            <div className='flex flex-col sm:flex-row items-start gap-y-1 justify-between'>
              <div className={'flex justify-end font-bold text-[1.4em] text-[#f9a928]'}>Invite by email</div>
            </div>
            <div>Send an email invitation to a colleague, granting them access to {process.env.REACT_APP_WEBSITE_NAME} as a project member.</div>
            <div className='opacity-60'>If the email is already associated with this account, they will be automatically added without the need to accept an invitation.</div>
          </div>
          <div className='flex flex-col w-full gap-4'>
            <div>
              <label className='block text-sm font-medium mb-1'>Email</label>
              <input 
                ref={inputEmailRef}
                list='suggestedEmails'
                className='w-full text-white rounded-md px-3 py-1.5 bg-[#444247] hover:bg-[#656565] focus:bg-[#656565]' 
                type='text' 
                value={inputEmail}
                onChange={(event) => { 
                  setInputEmail(event.target.value);
                }}
              />
              <datalist id='suggestedEmails'>
                {suggestedEmails.map((email, index) => (<option value={email} key={index} />))}
              </datalist>
            </div>
            <div>
              <label className='inline-block text-sm font-medium mb-1'>
                <button 
                  className='flex items-center gap-1 font-bold'
                  onClick={() => {
                    siteboardAction(SET_USER_ROLE_INFO_DIALOGUE_SHOWING, { isOpen: true });
                  }}
                >
                  Role<InfoIcon size='1.2em' />
                </button>
              </label>
              <div className='flex items-center gap-x-1.5 h-9'>
                <MemberRolePicker projectId={selectedProject.id} onChange={(role) => { setSelectedRole(role); }} />
              </div>
            </div>
          </div>
          <button  
            className={`relative flex justify-center items-center bg-[#471cff] hover:bg-[#603aff] py-2 font-bold cursor-pointer select-none rounded-lg`}
            disabled={inviteSentConfirmation || userAddedConfirmation || inputEmailAlreadyAdded || inputEmailAlreadyInvited}
            onClick={() => {
              try { 
                if (typeof inputEmail !== 'string' || inputEmail.length === 0) throw new Error('Please enter the new member\'s email');
                siteboardAction(ADD_PROJECT_MEMBER, {
                  projectId: selectedProject.id, 
                  subscriptionId: selectedProject.subscriptionId, 
                  email: inputEmail, 
                  role: selectedRole,
                }, () => { 
                  if(inputEmailIsSubscriptionMember) {
                    setUserAddedConfirmation(true);
                    setTimeout(() => { setUserAddedConfirmation(false); }, 3000);
                  } else {
                    setInviteSentConfirmation(true);
                    setTimeout(() => { setInviteSentConfirmation(false); }, 3000);
                  }
                  setInputEmail('');
                });
              } 
              catch(error) { 
                setAlert(error.message); 
              }
            }}
          >
            <div className='flex items-center justify-center gap-2'>
              {(inputEmailIsSubscriptionMember && !inputEmailAlreadyAdded) 
                ? <><AddIcon size='1.4em' color='#fff' /><p>Add to Project</p></>
                : <><SendIcon size='1.4em'/><p>Send Invite</p></>
              }
            </div>
            <div className={`absolute top-0 left-0 w-full h-full flex items-center justify-center bg-[#00bc7d] rounded-lg overflow-hidden pointer-events-none ${inviteSentConfirmation || userAddedConfirmation || inputEmailAlreadyAdded || inputEmailAlreadyInvited ? 'opacity-100' : 'opacity-0'} transition-all duration-300`}>
              {inviteSentConfirmation && <div className={`flex items-center justify-center gap-2 animate-right-fade`}><CircleCheckIcon size='1.6em' /> Invite Sent</div> }
              {userAddedConfirmation && <div className={`flex items-center justify-center gap-2 animate-right-fade`}><CircleCheckIcon size='1.6em' /> Added to Project</div> }
              {inputEmailAlreadyAdded && <div className={`flex items-center justify-center gap-2 animate-right-fade`}><CircleCheckIcon size='1.6em' /><p>Already Added</p></div>}
              {inputEmailAlreadyInvited && <div className={`flex items-center justify-center gap-2 animate-right-fade`}><CircleCheckIcon size='1.6em' /><p>Already Invited</p></div>}
            </div>
          </button>
        </div>

        {/*<div><b>Note:</b> Adding a user with a new email address will incur an additional monthly charge for a project seat. Charges only apply if the email hasn’t been used on your account before.</div>*/}

        <ProjectMembers project={selectedProject} />
      </div>
    </PopupWindow>
  );
};

// define redux state that will be passed in as props
const mapStateToProps = state => { return {
  _users: state.serverSynced.users,
  _projects: state.serverSynced.projects,
  _editProjectMembersDialogueId: state.localEphemeral.editProjectMembersDialogueId,
  _subscriptions: state.serverSynced.subscriptions,
}};
export default connect(mapStateToProps, { siteboardAction, setAlert })(AddProjectMemberDialogue);
